import React from 'react';
import { ICONS } from './constants';
import { XMarkIcon, ShieldCheckIcon, ChartBarIcon, GlobeAltIcon as GlobeIcon, CpuChipIcon } from '@heroicons/react/24/outline';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export const BusinessMethodologySidebar: React.FC<Props> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] transition-opacity animate-fade-in cursor-pointer"
                onClick={onClose}
            ></div>

            {/* Sidebar */}
            <div className={`fixed right-0 top-0 h-full w-full sm:w-[500px] bg-[#0B1121] border-l border-slate-700/50 shadow-2xl z-[101] transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'} overflow-y-auto flex flex-col`}>
                
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-slate-800 bg-slate-900/50 sticky top-0 z-10">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2 text-white">
                            <span className="text-indigo-400"><ShieldCheckIcon className="w-6 h-6" /></span>
                            Methodology & Confidence
                        </h2>
                        <p className="text-xs text-slate-400 uppercase tracking-widest mt-1">Intelligence Generation Framework</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-8 text-sm text-slate-300">
                    
                    {/* Confidence Intro */}
                    <div className="glass-panel p-5 border-l-4 border-l-indigo-500 bg-indigo-500/5">
                        <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                            <ChartBarIcon className="w-5 h-5 text-indigo-400" />
                            Analytical Confidence Summary
                        </h3>
                        <p className="leading-relaxed">
                            Our proprietary engine synthesizes multi-source intelligence to deliver high-confidence predictive analytics. By cross-referencing your live organizational asset footprint against thousands of global geopolitical signals, we maintain an <strong>industry-leading analytical confidence rating</strong> for strategic forecasting.
                        </p>
                    </div>

                    {/* Methodologies */}
                    <div className="space-y-6">
                        <h3 className="text-white font-bold uppercase tracking-widest border-b border-slate-800 pb-2 text-xs">Core Methodologies</h3>
                        
                        <div className="space-y-5">
                            <div className="flex gap-4">
                                <div className="mt-1 p-2 bg-emerald-500/10 text-emerald-400 rounded-lg shrink-0 h-fit">
                                    <GlobeIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-slate-200 mb-1 leading-none">PESTLE Alignment</h4>
                                    <p className="text-slate-400 text-sm leading-relaxed">
                                        Holistic evaluation of Political, Economic, Social, Technological, Legal, and Environmental factors affecting your specific regional footprint.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="mt-1 p-2 bg-amber-500/10 text-amber-400 rounded-lg shrink-0 h-fit">
                                    <ShieldCheckIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-slate-200 mb-1 leading-none">PMESII Structural Analysis</h4>
                                    <p className="text-slate-400 text-sm leading-relaxed">
                                        Tactical dissection of operational environments using Political, Military, Economic, Social, Information, and Infrastructure metrics to identify compounding threat vectors.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="mt-1 p-2 bg-red-500/10 text-red-400 rounded-lg shrink-0 h-fit">
                                    <ChartBarIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-slate-200 mb-1 leading-none">CARVER Target Vulnerability</h4>
                                    <p className="text-slate-400 text-sm leading-relaxed">
                                        Special Operations methodology rating Criticality, Accessibility, Recuperability, Vulnerability, Effect, and Recognizability to prioritize defensive resource allocation.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <div className="mt-1 p-2 bg-blue-500/10 text-blue-400 rounded-lg shrink-0 h-fit">
                                    <CpuChipIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-slate-200 mb-1 leading-none">AI-Assisted Synthesis</h4>
                                    <p className="text-slate-400 text-sm leading-relaxed">
                                        Advanced Large Language Models act as augmented analysts, ingesting vast OSINT data streams to identify non-obvious correlations without human bias.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Disclaimer / Endorsement */}
                    <div className="bg-slate-800/40 p-4 rounded-xl border border-slate-700/50 flex gap-4 mt-8">
                        <div className="text-slate-500 shrink-0">
                            {ICONS.shield}
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed font-mono">
                            These methodologies represent industry-standard best practices utilized by global intelligence and defense organizations. AI-assisted outputs should always be reviewed by assigned enterprise risk professionals.
                        </p>
                    </div>

                </div>
            </div>
        </>
    );
};
